import React from "react";

import Setting from "@app/analytics/account/components/Setting";
import SettingContent from "@app/analytics/account/components/SettingContent";
import SettingsModule from "@app/analytics/account/components/SettingsModule";
import SettingWithToggle from "@app/analytics/account/components/SettingWithToggle";
import { Divider } from "@app/analytics/account/components/styles";
import {
  SettingModule,
  SettingsToggleOnChange,
} from "@app/analytics/account/types";
import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import { useAuthenticated } from "@app/common/auth/useAuth";
import ROUTES from "@app/common/routes";

import InfoPanel from "@rui/InfoPanel";
import { Link } from "@rui/Link";

import useEmailPreferences from "./useEmailPreferences";

const EmailPreferencesModule = (props: SettingModule) => {
  const { user } = useAuthenticated();
  const {
    emailSubscriptions,
    productSubscriptions,
    handleEmailPreferenceUpdate,
    handleProductPreferenceUpdate,
  } = useEmailPreferences(user);
  const { updateUserSettings } = useUserSettings();

  const weeklyRecapsChecked = emailSubscriptions.includes("Weekly Recaps");
  const toggleWeeklyRecaps: SettingsToggleOnChange = (_e, checked) => {
    handleEmailPreferenceUpdate("Weekly Recaps", checked);
  };

  const productUpdatesChecked =
    productSubscriptions.includes("Product Updates");
  const toggleProductUpdates: SettingsToggleOnChange = (_e, checked) => {
    handleProductPreferenceUpdate("Product Updates", checked);
  };

  const accountInfoChecked = productSubscriptions.includes("Account Info");
  const toggleAccountUpdates: SettingsToggleOnChange = (_e, checked) => {
    handleProductPreferenceUpdate("Account Info", checked);
  };

  const toggleshowMeetingEmailThumbnails: SettingsToggleOnChange = (
    _e,
    checked
  ) => {
    updateUserSettings.mutate({ showMeetingEmailThumbnails: checked });
  };

  return (
    <SettingsModule {...props}>
      <Setting title="Your Email Subscriptions">
        <SettingContent spacing="4px">
          <Divider />
          <SettingWithToggle
            title="Weekly Recap"
            description="Get a summary of your meetings once a week"
            checked={weeklyRecapsChecked}
            onChange={toggleWeeklyRecaps}
          />
          <Divider />
          <SettingWithToggle
            title="Product Updates"
            description="Get product updates and announcements from Read"
            onChange={toggleProductUpdates}
            checked={productUpdatesChecked}
          />
          <Divider />
          <SettingWithToggle
            title="Account Info"
            description="Receive information from Read.ai about your account"
            onChange={toggleAccountUpdates}
            checked={accountInfoChecked}
          />
          <InfoPanel severity="info">
            Meeting Recap, Meeting Pre-Read, and Recommendation email
            preferences can be managed in{" "}
            <Link to={ROUTES.USER_SETTINGS_REPORTING}>Reporting</Link>
          </InfoPanel>
        </SettingContent>
      </Setting>
      <Divider />
      <Setting title="Email Preferences">
        <SettingContent spacing="4px">
          <Divider />
          <SettingWithToggle
            title="Meeting images"
            description="Display thumbnail images in Meeting Recap and Pre-Read emails for reports you own"
            onChange={toggleshowMeetingEmailThumbnails}
            checked={user.showMeetingEmailThumbnails ?? true}
          />
        </SettingContent>
      </Setting>
    </SettingsModule>
  );
};

export default EmailPreferencesModule;
